// frontend/src/components/planner/RecurringItemsPanel.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Pause,
  Play,
  Filter,
  Building2,
  ArrowLeftRight
} from 'lucide-react';
import api from '../../utils/api';
import AppShell from '../layout/AppShell';
import AddItemModal from './AddItemModal';
import AddTransferModal from './AddTransferModal';
import EditItemModal from './EditItemModal';
import LoadingSpinner from '../common/LoadingSpinner';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';

const FREQUENCY_LABELS = {
  once: 'One-time',
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly'
};

const TYPE_ICONS = {
  bill: Calendar,
  debt: CreditCard,
  income: TrendingUp
};

const TYPE_COLORS = {
  bill: { bg: 'bg-orange-100', text: 'text-orange-700', icon: 'text-orange-600' },
  debt: { bg: 'bg-red-100', text: 'text-red-700', icon: 'text-red-600' },
  income: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'text-emerald-600' }
};

export default function RecurringItemsPanel() {
  const { filterParam, activeFilter, entities } = useBusinessEntity();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all'); // all, bill, debt, income
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [addType, setAddType] = useState('bill');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [accountsList, setAccountsList] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  // Load accounts list on mount
  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    loadItems();
  }, [filterParam]);

  // Filter accounts dropdown based on entity selection
  const filteredAccountsList = useMemo(() => {
    if (activeFilter === 'all') return accountsList;
    if (activeFilter === 'personal') {
      const allLinkedIds = new Set();
      entities.forEach(e => (e.linkedAccountIds || []).forEach(id => allLinkedIds.add(id)));
      return accountsList.filter(acc => !allLinkedIds.has(acc.id));
    }
    const entity = entities.find(e => e.id === activeFilter);
    if (!entity) return accountsList;
    const linkedIds = new Set(entity.linkedAccountIds || []);
    return accountsList.filter(acc => linkedIds.has(acc.id));
  }, [accountsList, activeFilter, entities]);

  // Reset account selection when entity filter changes and selected account is no longer visible
  useEffect(() => {
    if (selectedAccountId && filteredAccountsList.length > 0) {
      const stillVisible = filteredAccountsList.some(acc => String(acc.id) === String(selectedAccountId));
      if (!stillVisible) {
        setSelectedAccountId('');
      }
    }
  }, [filteredAccountsList, selectedAccountId]);

  async function loadAccounts() {
    try {
      const response = await api.get('/api/planner/accounts');
      if (response.data.success) {
        setAccountsList(response.data.accounts || []);
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  }

  async function loadItems() {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams({ includeInactive: 'true' });
      if (filterParam) {
        params.append('businessEntityId', filterParam);
      }
      const response = await api.get(`/api/planner/recurring-items?${params.toString()}`);
      
      if (response.data.success) {
        setItems(response.data.items || []);
      } else {
        setError(response.data.error || 'Failed to load items');
      }
    } catch (err) {
      console.error('Error loading items:', err);
      setError('Failed to load recurring items');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/api/planner/recurring-items/${item.id}`);
      await loadItems();
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Failed to delete item');
    }
  }

  async function handleToggleActive(item) {
    try {
      await api.put(`/api/planner/recurring-items/${item.id}`, {
        is_active: !item.is_active
      });
      await loadItems();
    } catch (err) {
      console.error('Error toggling item:', err);
      alert('Failed to update item');
    }
  }

  function handleEdit(item) {
    setSelectedItem(item);
    setShowEditModal(true);
  }

  function handleAddNew(type) {
    setAddType(type);
    setShowAddModal(true);
  }

  function handleModalClose() {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowTransferModal(false);
    setSelectedItem(null);
  }

  function handleSuccess() {
    handleModalClose();
    loadItems();
  }

  const formatCurrency = (amount) => {
    const num = Math.abs(parseFloat(amount)) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    // Parse as local time to avoid timezone shift (dateStr is YYYY-MM-DD)
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatItemTiming = (item) => {
    if (item.is_variable) {
      // Show "~Monthly" or "~Weekly" etc. for variable items
      return `~${FREQUENCY_LABELS[item.frequency] || item.frequency}`;
    }
    return formatDate(item.next_due_date);
  };

  // Filter items by type and account
  const filteredItems = items.filter(item => {
    if (filter !== 'all' && item.item_type !== filter) return false;
    if (selectedAccountId && String(item.bank_account_id) !== String(selectedAccountId)) return false;
    return true;
  });

  // Calculate monthly equivalent amount based on frequency
  const getMonthlyAmount = (item) => {
    const amount = Math.abs(parseFloat(item.amount)) || 0;
    
    switch (item.frequency) {
      case 'weekly':
        return amount * 4;
      case 'biweekly':
        return amount * 2;
      case 'monthly':
        return amount;
      case 'quarterly':
      case 'yearly':
        // Exclude quarterly/yearly from monthly totals
        return 0;
      case 'once':
        // Only include one-time items if due this month
        if (!item.next_due_date && !item.start_date) return 0;
        const dueDate = new Date(item.next_due_date || item.start_date);
        const now = new Date();
        if (dueDate.getMonth() === now.getMonth() && dueDate.getFullYear() === now.getFullYear()) {
          return amount;
        }
        return 0;
      default:
        return amount;
    }
  };

  // Calculate totals with proper monthly equivalents (respects account filter)
  const totals = items.reduce((acc, item) => {
    if (!item.is_active) return acc;
    if (selectedAccountId && String(item.bank_account_id) !== String(selectedAccountId)) return acc;
    
    const monthlyAmount = getMonthlyAmount(item);
    
    if (item.item_type === 'income') {
      acc.income += monthlyAmount;
    } else {
      acc.expenses += monthlyAmount;
    }
    if (item.item_type === 'debt') {
      acc.debtBalance += parseFloat(item.remaining_balance) || 0;
    }
    return acc;
  }, { income: 0, expenses: 0, debtBalance: 0 });

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" text="Loading recurring items..." />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Bills & Income</h1>
            <p className="text-gray-600 mt-1">Manage your recurring transactions</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => handleAddNew('income')}
              className="flex items-center gap-2 px-4 py-2 min-h-[44px] lg:min-h-0 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              aria-label="Add income"
            >
              <Plus className="w-5 h-5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Income</span>
            </button>
            <button
              onClick={() => handleAddNew('bill')}
              className="flex items-center gap-2 px-4 py-2 min-h-[44px] lg:min-h-0 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              aria-label="Add bill"
            >
              <Plus className="w-5 h-5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Bill</span>
            </button>
            <button
              onClick={() => handleAddNew('debt')}
              className="flex items-center gap-2 px-4 py-2 min-h-[44px] lg:min-h-0 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              aria-label="Add debt"
            >
              <Plus className="w-5 h-5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Debt</span>
            </button>
            <button
              onClick={() => setShowTransferModal(true)}
              className="flex items-center gap-2 px-4 py-2 min-h-[44px] lg:min-h-0 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              aria-label="Add transfer"
            >
              <ArrowLeftRight className="w-5 h-5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Transfer</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Monthly Income</p>
                <p className="text-base sm:text-xl font-bold text-emerald-600">
                  {formatCurrency(totals.income)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingDown className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Monthly Expenses</p>
                <p className="text-base sm:text-xl font-bold text-orange-600">
                  {formatCurrency(totals.expenses)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <CreditCard className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Debt</p>
                <p className="text-base sm:text-xl font-bold text-red-600">
                  {formatCurrency(totals.debtBalance)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
            <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {['all', 'income', 'bill', 'debt'].map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 min-h-[44px] sm:min-h-0 transition-colors ${
                  filter === type
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
                {type !== 'all' && (
                  <span className="ml-1 text-xs opacity-75">
                    ({items.filter(i => i.item_type === type).length})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Account Filter */}
          {filteredAccountsList.length > 0 && (
            <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
              <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="flex-1 sm:flex-none min-w-0 px-3 py-2 sm:py-1.5 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">All Accounts</option>
                {filteredAccountsList.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.displayNameWithInstitution || acc.displayName}
                  </option>
                ))}
              </select>
              {selectedAccountId && (
                <button
                  onClick={() => setSelectedAccountId('')}
                  className="text-sm text-gray-500 hover:text-gray-700 min-h-[44px] sm:min-h-0 px-2 flex-shrink-0"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredItems.length === 0 && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {filter === 'all' ? 'No recurring items yet' : `No ${filter} items`}
            </h3>
            <p className="text-gray-600 mb-6">
              {filter === 'all' 
                ? 'Add your bills, income, and debts to start planning.'
                : `Add a ${filter} to track it in your projection.`}
            </p>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handleAddNew('income')}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Add Income
              </button>
              <button
                onClick={() => handleAddNew('bill')}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Add Bill
              </button>
            </div>
          </div>
        )}

        {/* Items List */}
        {filteredItems.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
            {filteredItems.map((item) => {
              const TypeIcon = TYPE_ICONS[item.item_type] || Calendar;
              const colors = TYPE_COLORS[item.item_type] || TYPE_COLORS.bill;
              
              const amountColor = item.item_type === 'income' ? 'text-emerald-600' : 'text-gray-900';
              return (
                <div
                  key={item.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${!item.is_active ? 'opacity-60' : ''}`}
                >
                  {/* Outer: stack on mobile, inline-row on desktop */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  {/* Content: icon + details (always horizontal) */}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg ${colors.bg} flex-shrink-0`}>
                      <TypeIcon className={`w-5 h-5 ${colors.icon}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Title + amount on the same line */}
                      <div className="flex items-baseline justify-between gap-3">
                        <h3 className="font-medium text-gray-900 truncate min-w-0">{item.name}</h3>
                        <span className={`text-base sm:text-lg font-semibold whitespace-nowrap flex-shrink-0 ${amountColor}`}>
                          {item.item_type === 'income' ? '+' : '-'}
                          {formatCurrency(item.amount)}
                        </span>
                      </div>

                      {/* Badges */}
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${colors.bg} ${colors.text} capitalize`}>
                          {item.item_type}
                        </span>
                        {item.categoryRef && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                            {item.categoryRef.name}
                          </span>
                        )}
                        {!item.is_active && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                            Paused
                          </span>
                        )}
                        {item.is_transfer && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                            Transfer
                          </span>
                        )}
                        {item.is_envelope && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-violet-100 text-violet-700">
                            Budget
                          </span>
                        )}
                        {item.is_variable && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
                            Flexible
                          </span>
                        )}
                      </div>

                      {/* Metadata — frequency, date, account, debt rate */}
                      <div className="flex items-center gap-x-3 gap-y-1 mt-1.5 text-sm text-gray-500 flex-wrap">
                        <span className="whitespace-nowrap">{FREQUENCY_LABELS[item.frequency] || item.frequency}</span>
                        <span aria-hidden="true" className="text-gray-300">•</span>
                        <span className={`whitespace-nowrap ${item.is_variable ? 'italic' : ''}`}>
                          {item.is_variable ? formatItemTiming(item) : `Next: ${formatItemTiming(item)}`}
                        </span>
                        {item.account_nickname && (
                          <>
                            <span aria-hidden="true" className="text-gray-300">•</span>
                            <span className="inline-flex items-center gap-1 whitespace-nowrap">
                              <Building2 className="w-3 h-3 flex-shrink-0" />
                              {item.account_nickname}
                            </span>
                          </>
                        )}
                        {item.item_type === 'debt' && item.interest_rate && (
                          <>
                            <span aria-hidden="true" className="text-gray-300">•</span>
                            <span className="whitespace-nowrap">{item.interest_rate}% APR</span>
                          </>
                        )}
                      </div>

                      {item.item_type === 'debt' && item.remaining_balance && (
                        <div className="text-sm text-red-600 mt-1">
                          Balance: {formatCurrency(item.remaining_balance)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions — new row below content on mobile, inline-right at sm: */}
                  <div className="flex items-center gap-1 justify-end mt-3 pt-3 border-t border-gray-100 sm:mt-0 sm:pt-0 sm:border-t-0 sm:flex-shrink-0">
                    <button
                      onClick={() => handleToggleActive(item)}
                      className="p-2 sm:p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                      title={item.is_active ? 'Pause' : 'Resume'}
                      aria-label={item.is_active ? 'Pause' : 'Resume'}
                    >
                      {item.is_active ? <Pause className="w-5 h-5 sm:w-4 sm:h-4" /> : <Play className="w-5 h-5 sm:w-4 sm:h-4" />}
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      className="p-2 sm:p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                      title="Edit"
                      aria-label="Edit"
                    >
                      <Edit2 className="w-5 h-5 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="p-2 sm:p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                      title="Delete"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-5 h-5 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddItemModal
          itemType={addType}
          onClose={handleModalClose}
          onSuccess={handleSuccess}
        />
      )}

      {showEditModal && selectedItem && (
        <EditItemModal
          item={selectedItem}
          onClose={handleModalClose}
          onSuccess={handleSuccess}
        />
      )}

      {showTransferModal && (
        <AddTransferModal
          onClose={() => setShowTransferModal(false)}
          onSuccess={() => { setShowTransferModal(false); loadItems(); }}
        />
      )}
    </AppShell>
  );
}
